import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Plus, Trash2 } from 'lucide-react';
import { usePlayer } from '../../context/PlayerContext';

const CalendarView = ({ onGameSelect, onDeleteGame, games }) => {
  const { playerData } = usePlayer();
  // Use passed games if available, otherwise fall back to all scheduled games
  const displayGames = games || playerData.scheduledGames || [];
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month'); // 'month', 'week', 'agenda'
  
  // Get current month and year
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  // Get first day of the month and total days in month
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  
  // Get day of week for first day (0 = Sunday, 6 = Saturday)
  const firstDayOfWeek = firstDayOfMonth.getDay();
  
  // Get days from previous month to fill the calendar
  const daysFromPrevMonth = firstDayOfWeek;
  const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
  
  // Get days needed from next month
  const totalCells = Math.ceil((daysInMonth + daysFromPrevMonth) / 7) * 7;
  const daysFromNextMonth = totalCells - (daysInMonth + daysFromPrevMonth);
  
  // Month names for display
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Day names for display
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Navigate to previous month
  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };
  
  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };
  
  // Navigate to today
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  // Change view type
  const changeView = (view) => {
    setCurrentView(view);
  };
  
  // Get color for game result
  const getGameColor = (game) => {
    if (!game.result) return 'bg-blue-100 text-blue-800'; // Upcoming game
    
    if (game.result === 'canceled' || game.result === 'postponed') {
      return 'bg-gray-100 text-gray-800'; // Canceled/Postponed
    }
    
    if (game.result === 'tie' || game.result === 'no-decision') {
      return 'bg-yellow-100 text-yellow-800'; // Tie/No Decision
    }
    
    if (game.result === 'win') {
      // Vary intensity based on margin of victory
      const margin = (game.teamScore || 0) - (game.opponentScore || 0);
      if (margin >= 5) return 'bg-green-200 text-green-800'; // Big win
      return 'bg-green-100 text-green-800'; // Regular win
    }
    
    if (game.result === 'loss') {
      // Vary intensity based on deficit
      const deficit = (game.opponentScore || 0) - (game.teamScore || 0);
      if (deficit >= 5) return 'bg-red-200 text-red-800'; // Big loss
      return 'bg-red-100 text-red-800'; // Regular loss
    }
    
    if (game.type === 'practice') {
      return 'bg-purple-100 text-purple-800'; // Practice
    }
    
    return 'bg-blue-100 text-blue-800'; // Default (upcoming)
  };
  
  // Get games for a specific date
  const getGamesForDate = (year, month, day) => {
    if (!displayGames || displayGames.length === 0) return [];
    
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return displayGames.filter(game => {
      // Handle different date formats
      let gameDate;
      if (typeof game.date === 'string') {
        if (game.date.includes('-')) {
          gameDate = game.date;
        } else if (game.date.includes('/')) {
          const [m, d, y] = game.date.split('/');
          gameDate = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        }
      }
      
      return gameDate === dateString;
    });
  };
  
  // Render games for a specific day
  const renderGamesForDay = (games, date) => {
    if (!games || games.length === 0) return null;
    
    return (
      <div className="space-y-1 mt-1">
        {games.map((game, index) => {
          // Determine game status and result
          const isPastGame = new Date(date) < new Date() && game.status === 'past';
          const gameResult = game.result || (isPastGame ? 'unknown' : null);
          
          return (
            <div 
              key={index}
              className={`text-xs p-1 rounded cursor-pointer ${getGameColor(game)}`}
              onClick={() => onGameSelect && onGameSelect(game)}
            >
              <div className="flex justify-between items-start">
                <div className="font-medium truncate">vs {game.opponent}</div>
                <div className="flex space-x-1">
                  <button 
                    className="text-green-600 hover:text-green-800 p-0.5"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Navigate to add stats page
                      window.location.href = `/add-game?gameId=${game.id}`;
                    }}
                    title="Add Stats"
                  >
                    <Plus size={10} />
                  </button>
                  <button 
                    className="text-red-600 hover:text-red-800 p-0.5"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onDeleteGame) {
                        onDeleteGame(game.id);
                      }
                    }}
                    title="Delete Game"
                  >
                    <Trash2 size={10} />
                  </button>
                </div>
              </div>
              
              {/* Show score for past games */}
              {isPastGame && game.teamScore !== undefined && game.opponentScore !== undefined && (
                <div className="text-xs">
                  {game.result === 'win' ? 'W' : game.result === 'loss' ? 'L' : 'T'} {game.teamScore}-{game.opponentScore}
                </div>
              )}
              
              {/* Show time for upcoming games */}
              {!isPastGame && (
                <div className="text-xs">{game.time}</div>
              )}
              
              {/* Home/Away indicator */}
              <div className="text-xs">
                {game.isHome ? 'Home' : 'Away'}
                {game.statEntryComplete && '✓'}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  
  // Render calendar grid for month view
  const renderMonthView = () => {
    const calendarDays = [];
    
    // Add days from previous month
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const date = new Date(currentYear, currentMonth - 1, day);
      const games = getGamesForDate(currentYear, currentMonth - 1, day);
      
      calendarDays.push(
        <div key={`prev-${day}`} className="border p-1 h-24 overflow-y-auto bg-gray-50 dark:bg-dark-300 text-gray-400 dark:text-darkText-tertiary">
          <div className="text-right mb-1">{day}</div>
          {renderGamesForDay(games, date)}
        </div>
      );
    }
    
    // Add days from current month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const games = getGamesForDate(currentYear, currentMonth, day);
      const isToday = new Date().toDateString() === date.toDateString();
      
      calendarDays.push(
        <div 
          key={`current-${day}`} 
          className={`border p-1 h-24 overflow-y-auto ${
            isToday 
              ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
              : 'bg-white dark:bg-dark-200'
          }`}
        >
          <div className={`text-right mb-1 ${
            isToday ? 'font-bold text-blue-600 dark:text-blue-400' : 'dark:text-darkText-primary'
          }`}>
            {day}
          </div>
          {renderGamesForDay(games, date)}
        </div>
      );
    }
    
    // Add days from next month
    for (let day = 1; day <= daysFromNextMonth; day++) {
      const date = new Date(currentYear, currentMonth + 1, day);
      const games = getGamesForDate(currentYear, currentMonth + 1, day);
      
      calendarDays.push(
        <div key={`next-${day}`} className="border p-1 h-24 overflow-y-auto bg-gray-50 dark:bg-dark-300 text-gray-400 dark:text-darkText-tertiary">
          <div className="text-right mb-1">{day}</div>
          {renderGamesForDay(games, date)}
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-7 gap-px">
        {/* Day headers */}
        {dayNames.map(day => (
          <div key={day} className="p-2 text-center font-medium bg-gray-100 dark:bg-dark-300 dark:text-darkText-primary">
            {day}
          </div>
        ))}
        
        {/* Calendar days */}
        {calendarDays}
      </div>
    );
  };
  
  // Render week view
  const renderWeekView = () => {
    // Get the start of the current week (Sunday)
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    const weekDays = [];
    
    // Generate 7 days for the week
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      
      const day = date.getDate();
      const month = date.getMonth();
      const year = date.getFullYear();
      
      const games = getGamesForDate(year, month, day);
      const isToday = new Date().toDateString() === date.toDateString();
      
      weekDays.push(
        <div 
          key={`week-${i}`} 
          className={`border p-2 ${
            isToday 
              ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
              : 'bg-white dark:bg-dark-200'
          }`}
        >
          <div className={`font-medium mb-2 ${
            isToday ? 'text-blue-600 dark:text-blue-400' : 'dark:text-darkText-primary'
          }`}>
            {dayNames[i]} {month + 1}/{day}
          </div>
          {renderGamesForDay(games, date)}
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-1 gap-1">
        {weekDays}
      </div>
    );
  };
  
  // Render agenda view
  const renderAgendaView = () => {
    // Get all games for the current month
    const games = [];
    
    // Get days in current month
    for (let day = 1; day <= daysInMonth; day++) {
      const gamesForDay = getGamesForDate(currentYear, currentMonth, day);
      if (gamesForDay.length > 0) {
        games.push({
          date: new Date(currentYear, currentMonth, day),
          games: gamesForDay
        });
      }
    }
    
    if (games.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500 dark:text-darkText-tertiary">
          No games scheduled for {monthNames[currentMonth]} {currentYear}
        </div>
      );
    }
    
    return (
      <div className="space-y-4">
        {games.map((dayData, index) => (
          <div key={index} className="border rounded-lg overflow-hidden dark:border-dark-300">
            <div className="bg-gray-100 dark:bg-dark-300 p-2 font-medium dark:text-darkText-primary">
              {dayData.date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </div>
            <div className="divide-y divide-gray-100 dark:divide-dark-300">
              {dayData.games.map((game, gameIndex) => {
                const isPastGame = new Date(dayData.date) < new Date() && game.status === 'past';
                
                return (
                  <div 
                    key={gameIndex} 
                    className="p-3 hover:bg-gray-50 dark:hover:bg-dark-300/50 cursor-pointer"
                    onClick={() => onGameSelect && onGameSelect(game)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium dark:text-darkText-primary">vs {game.opponent}</div>
                        <div className="text-sm text-gray-600 dark:text-darkText-secondary">
                          {game.time} • {game.location}
                        </div>
                      </div>
                      <div className={`px-2 py-1 rounded text-xs ${
                        game.isHome 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' 
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                      }`}>
                        {game.isHome ? 'HOME' : 'AWAY'}
                      </div>
                    </div>
                    
                    {/* Show game result if it's a past game */}
                    {isPastGame && game.teamScore !== undefined && game.opponentScore !== undefined && (
                      <div className={`mt-2 px-2 py-1 rounded text-xs inline-block ${getGameColor(game)}`}>
                        {game.result === 'win' ? 'WIN' : game.result === 'loss' ? 'LOSS' : 'TIE'} {game.teamScore}-{game.opponentScore}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <div className="bg-white dark:bg-dark-200 rounded-lg shadow-sm p-4 mb-4">
      {/* Calendar header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <h3 className="text-lg font-bold dark:text-darkText-primary">
            {monthNames[currentMonth]} {currentYear}
          </h3>
          <button 
            onClick={goToToday}
            className="ml-4 text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 px-2 py-1 rounded"
          >
            Today
          </button>
        </div>
        
        <div className="flex items-center">
          {/* View switcher */}
          <div className="mr-4 flex bg-gray-100 dark:bg-dark-300 rounded overflow-hidden">
            <button 
              onClick={() => changeView('month')}
              className={`px-3 py-1 text-sm ${
                currentView === 'month' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-700 dark:text-darkText-primary'
              }`}
            >
              Month
            </button>
            <button 
              onClick={() => changeView('week')}
              className={`px-3 py-1 text-sm ${
                currentView === 'week' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-700 dark:text-darkText-primary'
              }`}
            >
              Week
            </button>
            <button 
              onClick={() => changeView('agenda')}
              className={`px-3 py-1 text-sm ${
                currentView === 'agenda' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-700 dark:text-darkText-primary'
              }`}
            >
              Agenda
            </button>
          </div>
          
          {/* Navigation buttons */}
          <div className="flex">
            <button 
              onClick={goToPrevMonth}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-dark-300"
            >
              <ChevronLeft size={20} className="text-gray-600 dark:text-darkText-secondary" />
            </button>
            <button 
              onClick={goToNextMonth}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-dark-300"
            >
              <ChevronRight size={20} className="text-gray-600 dark:text-darkText-secondary" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Calendar legend */}
      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        <div className="flex items-center">
          <div className="w-3 h-3 rounded bg-green-100 mr-1"></div>
          <span className="dark:text-darkText-secondary">Win</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded bg-red-100 mr-1"></div>
          <span className="dark:text-darkText-secondary">Loss</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded bg-yellow-100 mr-1"></div>
          <span className="dark:text-darkText-secondary">Tie/No Decision</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded bg-blue-100 mr-1"></div>
          <span className="dark:text-darkText-secondary">Upcoming</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded bg-gray-100 mr-1"></div>
          <span className="dark:text-darkText-secondary">Canceled/Postponed</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded bg-purple-100 mr-1"></div>
          <span className="dark:text-darkText-secondary">Practice</span>
        </div>
      </div>
      
      {/* Calendar content */}
      <div className="overflow-x-auto">
        {currentView === 'month' && renderMonthView()}
        {currentView === 'week' && renderWeekView()}
        {currentView === 'agenda' && renderAgendaView()}
      </div>
      
      {/* Mini preview of adjacent months (only in month view) */}
      {currentView === 'month' && (
        <div className="grid grid-cols-2 gap-4 mt-4">
          {/* Previous month preview */}
          <div className="text-xs">
            <div className="font-medium mb-1 dark:text-darkText-primary">
              {monthNames[(currentMonth - 1 + 12) % 12]}
            </div>
            <div className="grid grid-cols-7 gap-px">
              {dayNames.map(day => (
                <div key={`prev-${day}`} className="text-center text-gray-400 dark:text-darkText-tertiary">
                  {day.charAt(0)}
                </div>
              ))}
              {/* Just show numbers 1-30 as placeholder */}
              {Array.from({ length: 30 }, (_, i) => (
                <div key={`mini-prev-${i}`} className="text-center text-gray-400 dark:text-darkText-tertiary">
                  {i + 1 <= new Date(currentYear, (currentMonth - 1 + 12) % 12 + 1, 0).getDate() ? i + 1 : ''}
                </div>
              ))}
            </div>
          </div>
          
          {/* Next month preview */}
          <div className="text-xs">
            <div className="font-medium mb-1 dark:text-darkText-primary">
              {monthNames[(currentMonth + 1) % 12]}
            </div>
            <div className="grid grid-cols-7 gap-px">
              {dayNames.map(day => (
                <div key={`next-${day}`} className="text-center text-gray-400 dark:text-darkText-tertiary">
                  {day.charAt(0)}
                </div>
              ))}
              {/* Just show numbers 1-30 as placeholder */}
              {Array.from({ length: 30 }, (_, i) => (
                <div key={`mini-next-${i}`} className="text-center text-gray-400 dark:text-darkText-tertiary">
                  {i + 1 <= new Date(currentYear, (currentMonth + 1) % 12 + 1, 0).getDate() ? i + 1 : ''}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
